import { useState, useEffect } from "react";
import { useUser } from "@/contexts/UserContext";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";
import { useCreateProviderEvaluation, calculateAverageScore } from "@/hooks/useShiftaskProviderEvaluation";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Clock, AlertTriangle, Users, UserCheck, MessageSquare, Laptop, FileText, Lightbulb, DollarSign, FileCheck, Star } from "lucide-react";
import { toast } from "sonner";

interface Metric {
  key: string;
  labelEn: string;
  labelEl: string;
  icon: any;
  section: 'service' | 'technology' | 'value';
}

const metrics: Metric[] = [
  { key: 'response_time', labelEn: 'Response Time', labelEl: 'Χρόνος Απόκρισης', icon: Clock, section: 'service' },
  { key: 'incident_handling', labelEn: 'Incident Handling', labelEl: 'Διαχείριση Συμβάντων', icon: AlertTriangle, section: 'service' },
  { key: 'guard_quality', labelEn: 'Guard Quality', labelEl: 'Ποιότητα Φρουρών', icon: Users, section: 'service' },
  { key: 'professionalism', labelEn: 'Professionalism', labelEl: 'Επαγγελματισμός', icon: UserCheck, section: 'service' },
  { key: 'communication', labelEn: 'Communication', labelEl: 'Επικοινωνία', icon: MessageSquare, section: 'service' },
  { key: 'technology_satisfaction', labelEn: 'Technology Satisfaction', labelEl: 'Ικανοποίηση από Τεχνολογία', icon: Laptop, section: 'technology' },
  { key: 'reporting_quality', labelEn: 'Reporting Quality', labelEl: 'Ποιότητα Αναφορών', icon: FileText, section: 'technology' },
  { key: 'innovation', labelEn: 'Innovation', labelEl: 'Καινοτομία', icon: Lightbulb, section: 'technology' },
  { key: 'value_for_money', labelEn: 'Value for Money', labelEl: 'Αξία για Χρήματα', icon: DollarSign, section: 'value' },
  { key: 'contract_flexibility', labelEn: 'Contract Flexibility', labelEl: 'Ευελιξία Συμβολαίου', icon: FileCheck, section: 'value' },
  { key: 'account_management', labelEn: 'Account Management', labelEl: 'Διαχείριση Λογαριασμού', icon: UserCheck, section: 'value' },
];

export const ClientProviderEvaluationForm = () => {
  const { user } = useUser();
  const { customization } = useAdminCustomization();
  const createMutation = useCreateProviderEvaluation();
  const appName = customization?.app_name || 'Provider';
  const [clientTeamId, setClientTeamId] = useState<string | null>(null);

  // Fetch client's team
  useEffect(() => {
    const fetchClientTeam = async () => {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('client_teams')
        .select('team_id')
        .eq('client_id', user.id)
        .maybeSingle();
      
      if (data && !error) {
        setClientTeamId(data.team_id);
      }
    };
    
    fetchClientTeam();
  }, [user?.id]);

  const [formData, setFormData] = useState({
    response_time_score: 3,
    response_time_comments: '',
    incident_handling_score: 3,
    incident_handling_comments: '',
    guard_quality_score: 3,
    guard_quality_comments: '',
    professionalism_score: 3,
    professionalism_comments: '',
    communication_score: 3,
    communication_comments: '',
    technology_satisfaction_score: 3,
    technology_satisfaction_comments: '',
    reporting_quality_score: 3,
    reporting_quality_comments: '',
    innovation_score: 3,
    innovation_comments: '',
    value_for_money_score: 3,
    value_for_money_comments: '',
    contract_flexibility_score: 3,
    contract_flexibility_comments: '',
    account_management_score: 3,
    account_management_comments: '',
    overall_satisfaction: 3,
    what_working_well: '',
    improvement_areas: '',
    additional_comments: '',
    would_recommend: true,
    likely_to_renew: true,
  });

  const calculateProgress = () => {
    const totalFields = metrics.length + 1; // metrics + overall
    let filledFields = metrics.filter(m => {
      const score = formData[`${m.key}_score` as keyof typeof formData];
      return typeof score === 'number' && score > 0;
    }).length;
    if (formData.overall_satisfaction > 0) filledFields++;
    return (filledFields / totalFields) * 100;
  };

  const averageScore = calculateAverageScore({
    ...formData,
    id: '',
    client_id: '',
    team_id: '',
    evaluation_year: 0,
    evaluation_period: '',
    submitted_at: '',
    created_at: '',
    updated_at: '',
  } as any);

  const handleSubmit = async () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    if (!clientTeamId) {
      toast.error('No team assigned to this client');
      return;
    }

    const currentYear = new Date().getFullYear();
    const currentQuarter = `Q${Math.floor(new Date().getMonth() / 3) + 1}`;

    try {
      await createMutation.mutateAsync({
        client_id: user.id,
        team_id: clientTeamId,
        evaluation_year: currentYear,
        evaluation_period: currentQuarter,
        ...formData,
      });
    } catch (error) {
      console.error('Failed to submit evaluation:', error);
    }
  };

  const getSectionMetrics = (section: string) => metrics.filter(m => m.section === section);

  const renderMetricSection = (sectionKey: string, titleEn: string, titleEl: string) => {
    const sectionMetrics = getSectionMetrics(sectionKey);
    
    return (
      <Card key={sectionKey} className="mb-6">
        <CardHeader>
          <CardTitle>{titleEl}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {sectionMetrics.map((metric) => {
            const Icon = metric.icon;
            const scoreKey = `${metric.key}_score` as keyof typeof formData;
            const commentsKey = `${metric.key}_comments` as keyof typeof formData;
            
            return (
              <div key={metric.key} className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <Label className="text-base font-medium">{metric.labelEl}</Label>
                </div>
                
                <div className="flex items-center gap-4">
                  <Slider
                    value={[formData[scoreKey] as number]}
                    onValueChange={(value) => setFormData({ ...formData, [scoreKey]: value[0] })}
                    min={1}
                    max={5}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-2xl font-bold min-w-[3rem] text-center">
                    {formData[scoreKey]}/5
                  </span>
                </div>
                
                <Textarea
                  placeholder="Προαιρετικά σχόλια..."
                  value={formData[commentsKey] as string}
                  onChange={(e) => setFormData({ ...formData, [commentsKey]: e.target.value })}
                  className="min-h-[80px]"
                />
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
        <CardHeader>
          <CardTitle className="text-2xl">Αξιολόγηση Παρόχου - {appName}</CardTitle>
          <CardDescription>
            Q{Math.floor(new Date().getMonth() / 3) + 1} {new Date().getFullYear()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Πρόοδος</Label>
              <Progress value={calculateProgress()} className="w-64" />
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary flex items-center gap-2">
                <Star className="w-8 h-8 fill-primary" />
                {averageScore.toFixed(1)}/5
              </div>
              <Label className="text-sm text-muted-foreground">Μέσος Όρος</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {renderMetricSection('service', 'Service Quality', 'Ποιότητα Υπηρεσιών')}
      {renderMetricSection('technology', 'Technology & Innovation', 'Τεχνολογία & Καινοτομία')}
      {renderMetricSection('value', 'Value & Relationship', 'Αξία & Σχέσεις')}

      <Card>
        <CardHeader>
          <CardTitle>Γενική Ικανοποίηση</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label className="text-base font-medium">Συνολική Ικανοποίηση</Label>
            <div className="flex items-center gap-4">
              <Slider
                value={[formData.overall_satisfaction]}
                onValueChange={(value) => setFormData({ ...formData, overall_satisfaction: value[0] })}
                min={1}
                max={5}
                step={1}
                className="flex-1"
              />
              <span className="text-2xl font-bold min-w-[3rem] text-center">
                {formData.overall_satisfaction}/5
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Τι λειτουργεί καλά;</Label>
            <Textarea
              value={formData.what_working_well}
              onChange={(e) => setFormData({ ...formData, what_working_well: e.target.value })}
              placeholder="Περιγράψτε τι λειτουργεί καλά..."
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-3">
            <Label>Περιοχές για βελτίωση</Label>
            <Textarea
              value={formData.improvement_areas}
              onChange={(e) => setFormData({ ...formData, improvement_areas: e.target.value })}
              placeholder="Περιγράψτε περιοχές που χρειάζονται βελτίωση..."
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-3">
            <Label>Πρόσθετα σχόλια</Label>
            <Textarea
              value={formData.additional_comments}
              onChange={(e) => setFormData({ ...formData, additional_comments: e.target.value })}
              placeholder="Οποιαδήποτε άλλα σχόλια..."
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-base">Θα συνιστούσατε τον {appName};</Label>
              <Switch
                checked={formData.would_recommend}
                onCheckedChange={(checked) => setFormData({ ...formData, would_recommend: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-base">Πιθανότητα ανανέωσης συμβολαίου;</Label>
              <Switch
                checked={formData.likely_to_renew}
                onCheckedChange={(checked) => setFormData({ ...formData, likely_to_renew: checked })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button 
        onClick={handleSubmit} 
        className="w-full" 
        size="lg"
        disabled={createMutation.isPending || !clientTeamId}
      >
        {createMutation.isPending ? 'Υποβολή...' : !clientTeamId ? 'Δεν υπάρχει ομάδα' : 'Υποβολή Αξιολόγησης'}
      </Button>
    </div>
  );
};
